import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles, Loader2, RotateCcw } from "lucide-react";
import { API } from "@/lib/api";
import api from "@/lib/api";

function newSessionId() {
  try {
    return crypto.randomUUID();
  } catch {
    return "sess-" + Math.random().toString(36).slice(2) + Date.now();
  }
}

export default function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    const s = localStorage.getItem("hw_assistant_session");
    if (s) return s;
    const n = newSessionId();
    localStorage.setItem("hw_assistant_session", n);
    return n;
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (open && !loadedRef.current) {
      loadedRef.current = true;
      api
        .get(`/assistant/history?session_id=${encodeURIComponent(sessionId)}`)
        .then(({ data }) => setMessages(data.messages || []))
        .catch(() => {});
    }
  }, [open, sessionId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    await sendText(text);
  };

  const sendText = async (text) => {
    if (!text || busy) return;
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const token = localStorage.getItem("tp_token");
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          let payload;
          try {
            payload = JSON.parse(line.slice(5).trim());
          } catch {
            continue;
          }
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.delta };
              return copy;
            });
          } else if (payload.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: payload.error };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        if (copy.length && copy[copy.length - 1].role === "assistant" && !copy[copy.length - 1].content) {
          copy[copy.length - 1] = { role: "assistant", content: "Errore di connessione, riprova." };
        }
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const resetChat = async () => {
    try {
      await api.delete(`/assistant/history?session_id=${encodeURIComponent(sessionId)}`);
    } catch {
      /* ignore */
    }
    const n = newSessionId();
    localStorage.setItem("hw_assistant_session", n);
    setSessionId(n);
    setMessages([]);
  };

  return (
    <>
      {/* Floating button — always visible, bottom-right (above mobile nav) */}
      <button
        data-testid="assistant-fab"
        onClick={() => setOpen((o) => !o)}
        aria-label="Assistente ChatGPT"
        className="fixed z-[60] right-4 bottom-24 md:bottom-6 h-14 w-14 rounded-full tp-brand-gradient shadow-lg shadow-fuchsia-600/40 flex items-center justify-center text-white hover:scale-105 active:scale-95 transition-transform"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {open && (
        <div
          data-testid="assistant-panel"
          className="fixed z-[60] right-4 bottom-40 md:bottom-24 w-[calc(100vw-2rem)] max-w-sm h-[70vh] max-h-[560px] flex flex-col rounded-2xl overflow-hidden tp-glass-strong border border-white/10 shadow-2xl"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-white/[0.03]">
            <div className="flex items-center gap-2 text-white">
              <span className="h-7 w-7 rounded-lg tp-brand-gradient flex items-center justify-center">
                <Sparkles size={15} />
              </span>
              <div className="leading-tight">
                <div className="text-sm font-semibold">Assistente</div>
                <div className="text-[10px] text-zinc-400">ChatGPT · GPT-5</div>
              </div>
            </div>
            <button data-testid="assistant-reset" onClick={resetChat} title="Nuova chat"
              className="p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5">
              <RotateCcw size={15} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
            {messages.length === 0 && (
              <div className="text-center text-zinc-400 text-sm mt-8 px-4">
                Ciao! Sono il tuo assistente. Chiedimi di scrivere un'email, riassumere le task o darti idee di marketing.
                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  <button
                    data-testid="assistant-shortcut-social"
                    disabled={busy}
                    onClick={() => sendText("Dammi 5 idee di post per i social media, con un breve spunto per ciascuno.")}
                    className="inline-flex items-center gap-1.5 rounded-full border border-fuchsia-500/30 bg-fuchsia-500/10 px-3 py-1.5 text-xs text-fuchsia-100 hover:bg-fuchsia-500/20 transition-colors disabled:opacity-40"
                  >
                    <Sparkles size={12} /> Idee post social
                  </button>
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`assistant-msg-${m.role}`}
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                    m.role === "user"
                      ? "tp-brand-gradient text-white rounded-br-sm"
                      : "bg-white/[0.06] text-zinc-100 border border-white/10 rounded-bl-sm"
                  }`}
                >
                  {m.content || (busy && i === messages.length - 1 ? <Loader2 size={15} className="animate-spin" /> : "")}
                </div>
              </div>
            ))}
          </div>

          <div className="p-3 border-t border-white/10 bg-white/[0.03]">
            <div className="flex items-end gap-2">
              <textarea
                data-testid="assistant-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                rows={1}
                placeholder="Scrivi un messaggio…"
                className="flex-1 resize-none max-h-28 rounded-xl bg-white/[0.05] border border-white/10 px-3 py-2 text-sm text-white placeholder:text-zinc-500 outline-none focus:border-fuchsia-500/50"
              />
              <button
                data-testid="assistant-send"
                onClick={send}
                disabled={busy || !input.trim()}
                className="h-10 w-10 shrink-0 rounded-xl tp-brand-gradient text-white flex items-center justify-center disabled:opacity-40"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
